/**
 * Parses knowledge-base citation strings embedded in markdown footnotes (same shape as agents/chat).
 *
 *   s3://<bucket>/.files-kb-<accountId>/<projectId>/<file-service-path>[?page=N]
 */

export type KnowledgeBaseCitation = {
  citationType: 'KnowledgeBase';
  accountId: string;
  projectId: string;
  /** Full file-service `name` (path within the project). */
  name: string;
  /** Basename of `name`, used as the user-visible label. */
  displayName: string;
  sourceLabel: string;
  title: string;
  page?: number;
};

const S3_KB_URI_RE = /^s3:\/\/[^/]+\/\.files-kb-([^/]+)\/([^/]+)\/(.+)$/;

const parsePagePositiveInt = (
  query: string | undefined,
): number | undefined => {
  if (!query) {
    return undefined;
  }
  const params = new URLSearchParams(query);
  const raw = params.get('page');
  if (typeof raw !== 'string' || !/^\d+$/.test(raw)) {
    return undefined;
  }
  const value = parseInt(raw, 10);
  return value > 0 ? value : undefined;
};

export const parseKnowledgeBaseCitation = (
  reference: string,
): KnowledgeBaseCitation | null => {
  const trimmed = reference.trim();
  if (!trimmed) {
    return null;
  }

  const queryIndex = trimmed.indexOf('?');
  const base = queryIndex === -1 ? trimmed : trimmed.slice(0, queryIndex);
  const query = queryIndex === -1 ? undefined : trimmed.slice(queryIndex + 1);

  const match = S3_KB_URI_RE.exec(base);
  if (!match) {
    return null;
  }
  const [, accountId, projectId, name] = match;
  if (!accountId || !projectId || !name) {
    return null;
  }

  const displayName = name.split('/').pop() || name;
  const page = parsePagePositiveInt(query);

  return {
    citationType: 'KnowledgeBase',
    accountId,
    projectId,
    name,
    displayName,
    sourceLabel: 'Knowledge base',
    page,
    title: `${displayName} — Knowledge base`,
  };
};

export const isBrowserPreviewableKnowledgeBaseMime = (
  mime: string | undefined,
): boolean => {
  if (!mime) {
    return true;
  }
  const m = mime.toLowerCase();
  return (
    m === 'application/pdf' || m.startsWith('image/') || m.startsWith('text/')
  );
};

/**
 * CSV detection accepts the canonical `text/csv` MIME and a couple of common
 * variants we've seen in the wild (e.g., `application/csv`, `application/vnd.ms-excel`
 * served by some uploaders), plus a `.csv` filename fallback for cases where
 * file-service returns a generic mime such as `application/octet-stream`.
 */
export const isCsvMime = (mime: string | undefined): boolean => {
  if (!mime) {
    return false;
  }
  const m = mime.toLowerCase().split(';')[0].trim();
  return (
    m === 'text/csv' ||
    m === 'application/csv' ||
    m === 'application/vnd.ms-excel' ||
    m === 'text/x-csv' ||
    m === 'text/comma-separated-values'
  );
};

export const hasCsvExtension = (name: string | undefined): boolean => {
  if (!name) {
    return false;
  }
  const cleaned = name.split('?')[0].split('#')[0];
  return /\.csv$/i.test(cleaned);
};

/**
 * True when the citation should be rendered in-app as a CSV table instead of
 * opened (and inevitably downloaded) by the browser. We use mime first when
 * available; otherwise fall back to the citation `name` which is the file's
 * path within the project.
 */
export const isCsvKnowledgeBaseFile = (params: {
  mime?: string;
  name?: string;
}): boolean => isCsvMime(params.mime) || hasCsvExtension(params.name);
